import React, { useEffect, useRef } from "react";
import * as d3 from "d3";
import ConnectedStackBarSkeleton from "./ConnectedStackBarSkeleton";
import { formatFixedOneNotation, formatIndianNumber } from "components/Reports/DashboardComponents/Home/StoreListCards/data";
import { calculateGrowth, COLOR_PALETTE_LIST } from "components/Reports/data";


const ConnectedStackedBarChart = ({ data = [], width = 700, height = 400, loading, colors, isDotConnected = true, isLegends = false, kConvert = [] }) => {
  const ref = useRef();

  useEffect(() => {
    if (!data || data.length === 0) return;
    const svg = d3.select(ref.current);
    svg.selectAll("*").remove();

    const margin = { top: 60, right: 40, bottom: 50, left: 50 };
    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    const g = svg
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const categories = [...new Set(data.map((d) => d.category))];
    const subs = [...new Set(data.map((d) => d.sub))];

    const xScale = d3
      .scaleBand()
      .domain(categories)
      .range([0, chartWidth])
      .padding(0.4);

    const color = d3
      .scaleOrdinal()
      .domain(subs)
      .range(colors);

    // Group by category
    const grouped = d3
      .rollups(
        data,
        (v) => v,
        (d) => d.category
      )
      .map(([key, values]) => ({ category: key, values }));

    // Stack generator
    const stackGen = d3
      .stack()
      .keys(subs)
      .value((d, key) => {
        const found = d.values.find((v) => v.sub === key);
        return found ? found.value : 0;
      });

    const stackedSeries = stackGen(grouped);

    const yMax = d3.max(stackedSeries[stackedSeries.length - 1], (d) => d[1]);
    const yScale = d3.scaleLinear().domain([0, yMax]).range([chartHeight, 15]);

    // Map for easy lookup
    const stackMap = {};
    grouped.forEach((catObj) => (stackMap[catObj.category] = {}));
    stackedSeries.forEach((layer) => {
      layer.forEach((seg) => {
        const cat = seg.data.category;
        stackMap[cat][layer.key] = { y0: seg[0], y1: seg[1] };
      });
    });

    // Tooltip
    const tooltip = d3
      .select("body")
      .append("div")
      .style("position", "absolute")
      .style("background", "var(--color-tooltip-background)")
      .style("padding", "6px 10px")
      .style("border-radius", "6px")
      .style("font-size", "var(--font-size-tooltip)")
      .style("color", "var(--color-tooltip)")
      .style("pointer-events", "none")
      .style("opacity", 0);

    // Draw bars + labels
    g.selectAll(".bar-group")
      .data(categories)
      .join("g")
      .attr("class", "bar-group")
      .attr("transform", (d) => `translate(${xScale(d)},0)`)
      .each(function (cat, i) {
        const group = d3.select(this);
        subs.forEach((sub) => {
          const seg = stackMap[cat][sub];
          if (!seg) return;

          const value = seg.y1 - seg.y0;
          // const growth = (Math.random() * 40 - 10).toFixed(1); // demo growth %
          const original = data.find(
            (d) => d.category === cat && d.sub === sub
          );
          const catTotalTY = d3.sum(
            grouped.find(g => g.category === cat).values,
            v => v.ty_amt
          );

          const contri = ((original.ty_amt / catTotalTY) * 100).toFixed(1)
          // console.log("asdgcasdjabsjdbsa", catTotalTY)
          const growth = calculateGrowth(original?.ly_amt, original?.ty_amt)
          // Rect
          group
            .append("rect")
            .attr("x", 0)
            .attr("y", yScale(seg.y1))
            .attr("width", xScale.bandwidth())
            .attr("height", yScale(seg.y0) - yScale(seg.y1))
            .attr("fill", color(sub))
            .style("cursor", "default")
            .on("mouseover", (e) => {
              tooltip
                .style("opacity", 1)
                .html(
                  `<span>${cat}</span><br>${sub} <b>(${contri}%)</b><br>TY: ${formatFixedOneNotation(original?.ty_amt, cat === "QTY" ? "qty" :"value") || 0} <span style="color:${cat === "DISC" && growth < 0 ? "green" : growth > 0 ? "green" : "red"}; ;">(${growth}%)</span><br>LY: ${formatFixedOneNotation(original?.ly_amt, cat === "QTY" ? "qty" :"value") || 0}`
                );
            })
            .on("mousemove", (e) => {
              tooltip
                .style("left", e.pageX + 10 + "px")
                .style("top", e.pageY - 20 + "px");
            })
            .on("mouseout", () => tooltip.style("opacity", 0));

          const segmentHeight = yScale(seg.y0) - yScale(seg.y1);
          const minHeightToShow = 14;

          if (segmentHeight >= minHeightToShow) {
            const barWidth = xScale.bandwidth();
            const textValue = `${i === 0 && !isLegends ? sub : ""} (${(original?.growth).toFixed(1)}%)`;

            let fontSize = 12;

            function truncateTextWithEllipsis(text, maxWidth) {
              const temp = d3
                .select("body")
                .append("span")
                .style("position", "absolute")
                .style("visibility", "hidden")
                .style("font-size", "var(--font-size-body2")
                .text(text);

              let width = temp.node().getBoundingClientRect().width;

              if (width <= maxWidth) {
                temp.remove();
                return text;
              }

              let truncated = text;
              while (width > maxWidth && truncated.length > 1) {
                truncated = truncated.slice(0, -1); // remove last char
                temp.text(truncated + "...");
                width = temp.node().getBoundingClientRect().width;
              }

              temp.remove();

              return truncated.length > 1 ? truncated + "..." : "...";
            }

            // 🔥 Apply ellipsis truncation
            let truncatedText = truncateTextWithEllipsis(textValue, barWidth - 4);

            group.append("text")
              .attr("x", barWidth / 2)
              .attr("y", (yScale(seg.y1) + yScale(seg.y0)) / 2 + fontSize * 0.35)
              .attr("text-anchor", "middle")
              .style("fill", "#ffffffff")
              .style("font-size", "var(--font-size-body2")
              .text(truncatedText);
          }
        });
      });

    // Total value on top
    grouped.forEach((catObj) => {
      // Total height of the bar (based on VALUE)
  const total = d3.sum(catObj.values, (v) => v.value);

  // Total amount you want to show (TY AMT)
  const totalTyAmt = d3.sum(catObj.values, (v) => v.ty_amt);
  // console.log("catObj", catObj)

      g.append("text")
        .attr("x", xScale(catObj.category) + xScale.bandwidth() / 2)
        .attr("y", yScale(total) - 8)
        .attr("text-anchor", "middle")
        .style("font-size", "var(--font-size-body3)")
        .style("font-weight", "600")
        .style("fill", "#333")
        .text(formatFixedOneNotation(totalTyAmt, kConvert.includes(catObj?.category) ? "qty" : "value"));
    });

    // ---------------- RIGHT HORIZONTAL, RESPONSIVE LEGENDS ----------------
    const legendGroup = svg.append("g").attr("class", "legend-group");

    const legendPadding = 20; // gap between items
    let currX = 0;
    let currY = margin.top - 50;  // top area
    const availableWidth = width - margin.right - 10; // usable right area

    // temp g to measure width
    const measureGroup = legendGroup.append("g");

    isLegends && (subs.forEach(sub => {
      const g = measureGroup.append("g");

      g.append("rect")
        .attr("width", 12)
        .attr("height", 12)
        .attr("rx", 2)
        .attr("ry", 2)
        .attr("fill", color(sub));

      g.append("text")
        .attr("x", 18)
        .attr("y", 10)
        .style("font-size", "var(--font-size-body3)")
        .text(sub);

      const itemWidth = g.node().getBBox().width;
      const itemHeight = g.node().getBBox().height;

      // Wrap to next row if exceeding width
      if (currX + itemWidth > availableWidth) {
        currX = 0;
        currY += itemHeight + 6; // new row
      }

      legendGroup.append("g")
        .attr("transform", `translate(${width - margin.right - itemWidth - currX}, ${currY})`)
        .each(function () {
          const lg = d3.select(this);

          lg.append("rect")
            .attr("width", 12)
            .attr("height", 12)
            .attr("rx", 2)
            .attr("ry", 2)
            .attr("fill", color(sub));

          lg.append("text")
            .attr("x", 18)
            .attr("y", 10)
            .style("font-size", "var(--font-size-body3)")
            .text(sub);
        });

      currX += itemWidth + legendPadding;

      g.remove(); // remove temp
    }))

    // Dotted connectors (only between segments, not top)
    const gap = xScale.bandwidth() / 2;
    isDotConnected && (
    subs.slice(0, subs.length - 1).forEach((sub) => {
      const topPoints = categories.map((cat) => {
        const seg = stackMap[cat][sub];
        return {
          x: xScale(cat) + xScale.bandwidth() / 2,
          y: yScale(seg.y1),
        };
      });

      for (let i = 0; i < topPoints.length - 1; i++) {
        g.append("line")
          .attr("x1", topPoints[i].x + gap)
          .attr("y1", topPoints[i].y) // slightly above segment
          .attr("x2", topPoints[i + 1].x - gap)
          .attr("y2", topPoints[i + 1].y)
          .attr("stroke", "#999")
          .attr("stroke-opacity", 1)
          .attr("stroke-dasharray", "3 3")
          .attr("fill", "none");
      }
    }))

    // X-axis labels
    g.selectAll(".cat-label")
      .data(categories)
      .join("text")
      .attr("class", "cat-label")
      .attr("x", (d) => xScale(d) + xScale.bandwidth() / 2)
      .attr("y", chartHeight + 25)
      .attr("text-anchor", "middle")
      .style("font-weight", "bold")
      .style("font-size", "var(--font-size-body3)")
      .text((d) => d);

    return () => tooltip.remove();
  }, [data, width, height]);

  if (loading) return <ConnectedStackBarSkeleton width={width} height={height} />
  return <svg ref={ref} width={width} height={height}></svg>;
};

export default ConnectedStackedBarChart;
